/**
 * Hook para buscar conversas de email com infinite scroll
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ConversationListResponse } from '@troia-v3/types';
import { getEmailConversations, getEmailConversation } from '../services/emailService';
import type { EmailFolderType } from '../types/email.types';

interface UseEmailConversationsOptions {
  folder: EmailFolderType;
  limit?: number;
  enabled?: boolean;
  search?: string;
  additionalFilters?: Record<string, unknown>;
}

export const useEmailConversations = ({
  folder,
  limit = 25,
  enabled = true,
  search,
  additionalFilters,
}: UseEmailConversationsOptions) => {
  const mergedFilters = {
    ...(search ? { search } : {}),
    ...(additionalFilters || {}),
  };
  const hasFilters = Object.keys(mergedFilters).length > 0;

  return useInfiniteQuery<ConversationListResponse>({
    queryKey: ['email-conversations', folder, search, additionalFilters],
    queryFn: ({ pageParam, signal }) =>
      getEmailConversations(
        folder,
        pageParam as number,
        limit,
        hasFilters ? mergedFilters : undefined,
        signal
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const currentPage = lastPage.page || 1;
      const totalPages = lastPage.totalPages || 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useEmailConversation = (id?: string) => {
  return useQuery({
    queryKey: ['email-conversation', id],
    queryFn: ({ signal }) => getEmailConversation(id!, signal),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
};
